import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { getApiErrorMessage } from '../../services/api';
import {
    COMPETITOR_DNI_PARAM,
    getCompetitorDniFromSearch,
    hasInvalidCompetitorParam,
} from '../../utils/competitor';

export default function BuyerDetails() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const linkedCompetitorDni = getCompetitorDniFromSearch(searchParams);
    const invalidCompetitorParam = hasInvalidCompetitorParam(searchParams);
    const competitorQuery = linkedCompetitorDni
        ? `?${COMPETITOR_DNI_PARAM}=${linkedCompetitorDni}`
        : '';
    const [buyerInfo, setBuyerInfo] = useState({
        buyer_fullname: '',
        buyer_email: '',
        buyer_phone: '',
        buyer_area_code: '',
        quantity: 1,
        discount_code: '',
        document_type: 'DNI',
        document_number: '',
        buyer_street_name: '',
        buyer_street_number: '',
        buyer_zip_code: '',
    });
    const [attendees, setAttendees] = useState([{ dni: '', name: '' }]);
    const [competitorDni, setCompetitorDni] = useState(linkedCompetitorDni);
    const [showAddress, setShowAddress] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [event, setEvent] = useState(null);

    const selectedEventId = localStorage.getItem('selectedEventId');

    const getMaxTickets = (ev) => {
        if (ev && ev.free_tickets_threshold && ev.max_free_tickets_per_purchase) {
            return ev.free_tickets_threshold + ev.max_free_tickets_per_purchase;
        }
        return 10;
    };

    const hasFreeRule = (ev) => {
        return ev && ev.free_tickets_threshold && ev.max_free_tickets_per_purchase;
    };

    useEffect(() => {
        if (!selectedEventId) {
            navigate(`/sales${competitorQuery}`);
            return;
        }
        const fetchEvent = async () => {
            try {
                const res = await api.get('/events');
                const found = res.data.find(e => String(e.id) === String(selectedEventId));
                if (found) {
                    setEvent(found);
                    setBuyerInfo(prev => ({ ...prev, quantity: 1 }));
                    setAttendees([{ dni: '', name: '' }]);
                }
            } catch (err) {
                console.error('Error fetching event', err);
            }
        };
        fetchEvent();
    }, [navigate, selectedEventId, competitorQuery]);

    useEffect(() => {
        if (linkedCompetitorDni) {
            setCompetitorDni(linkedCompetitorDni);
        }
    }, [linkedCompetitorDni]);

    useEffect(() => {
        const qty = parseInt(buyerInfo.quantity, 10) || 1;
        const max = getMaxTickets(event);
        const clamped = Math.min(Math.max(qty, 1), max);
        if (clamped !== qty) {
            setBuyerInfo(prev => ({ ...prev, quantity: clamped }));
        }
        setAttendees(prev => {
            const newArr = [];
            for (let i = 0; i < clamped; i++) {
                newArr.push(prev[i] || { dni: '', name: '' });
            }
            return newArr;
        });
    }, [buyerInfo.quantity, event]);

    const handleProceed = async (e) => {
        e.preventDefault();
        setFieldErrors({});
        if (
            !buyerInfo.buyer_fullname ||
            !buyerInfo.buyer_email ||
            !buyerInfo.buyer_phone ||
            !buyerInfo.buyer_area_code ||
            !buyerInfo.document_number
        ) {
            setError("Por favor completa los campos requeridos.");
            return;
        }

        const qty = parseInt(buyerInfo.quantity, 10);
        const max = getMaxTickets(event);
        if (qty < 1 || qty > max) {
            setError(`La cantidad debe ser entre 1 y ${max}.`);
            return;
        }

        for (let i = 0; i < attendees.length; i++) {
            if (!attendees[i].dni || !/^\d{7,10}$/.test(attendees[i].dni)) {
                setError(`La entrada ${i + 1} debe tener un DNI valido (7-10 digitos).`);
                return;
            }
            const attendeeName = (attendees[i].name || '').trim();
            if (!attendeeName) {
                setError(`La entrada ${i + 1} debe tener el nombre del asistente.`);
                return;
            }
            if (attendeeName.length > 255) {
                setError(`El nombre de la entrada ${i + 1} no puede superar los 255 caracteres.`);
                return;
            }
        }
        const dniSet = new Set(attendees.map(a => a.dni));
        if (dniSet.size !== attendees.length) {
            setError("No se puede repetir el DNI de una entrada.");
            return;
        }

        setError('');
        setLoading(true);

        try {
            const body = {
                event_id: selectedEventId,
                fullname: buyerInfo.buyer_fullname,
                buyer_email: buyerInfo.buyer_email,
                buyer_phone: buyerInfo.buyer_phone,
                buyer_area_code: buyerInfo.buyer_area_code,
                quantity: qty,
                document_type: buyerInfo.document_type || 'DNI',
                document_number: buyerInfo.document_number,
                attendees: attendees.map((att) => ({
                    dni: att.dni,
                    name: (att.name || '').trim(),
                })),
            };

            if (buyerInfo.discount_code) {
                body.discount_code = buyerInfo.discount_code;
            }
            if (competitorDni && /^\d{7,10}$/.test(competitorDni)) {
                body.competitor_dni = competitorDni;
            }
            if (buyerInfo.buyer_street_name) {
                body.buyer_street_name = buyerInfo.buyer_street_name;
            }
            if (buyerInfo.buyer_street_number) {
                body.buyer_street_number = buyerInfo.buyer_street_number;
            }
            if (buyerInfo.buyer_zip_code) {
                body.buyer_zip_code = buyerInfo.buyer_zip_code;
            }

            const response = await api.post('/sales/checkout', body);

            if (response.data && response.data.init_point) {
                window.location.href = response.data.init_point;
            } else {
                throw new Error("No se recibio link de pago.");
            }
        } catch (err) {
            const data = err.response?.data;
            if (err.response?.status === 422 && data?.errors) {
                setFieldErrors(data.errors);
            }
            setError(getApiErrorMessage(err, 'Error al procesar la compra.'));
            setLoading(false);
        }
    };

    const maxTickets = getMaxTickets(event);
    const showFreeRule = hasFreeRule(event);

    const getFieldError = (key) => {
        const value = fieldErrors[key];
        return Array.isArray(value) ? value[0] : value;
    };

    return (
        <div className="max-w-2xl mx-auto min-h-[70vh] flex items-center justify-center py-6">
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-xl w-full border border-gray-100 transition-all hover:shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                    <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
                        Paso 2 de 2
                    </span>
                    <span className="text-xs text-gray-500 font-medium">Informacion de Pago</span>
                </div>

                <h2 className="text-2xl font-bold mb-4 text-center text-gray-800">Datos del comprador</h2>
                {event && (
                    <div className="mb-4 text-center">
                        <p className="text-sm text-gray-600">Evento: <span className="font-semibold">{event.title}</span></p>
                        {showFreeRule && (
                            <p className="text-xs text-green-600">A partir de {event.free_tickets_threshold} entradas, el resto son gratis (maximo {maxTickets} por compra)</p>
                        )}
                        {!showFreeRule && <p className="text-xs text-gray-600">Maximo {maxTickets} entradas por compra</p>}
                    </div>
                )}

                {error && (
                    <div className="mb-6 text-red-600 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg text-sm flex items-center gap-2">
                        <span>{error}</span>
                    </div>
                )}

                <form onSubmit={handleProceed} className="space-y-6">
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-700 border-b pb-2">Datos Personales</h3>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Nombre Completo</label>
                            <input required type="text" className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg" value={buyerInfo.buyer_fullname} onChange={e => setBuyerInfo({ ...buyerInfo, buyer_fullname: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</label>
                            <input required type="email" className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg" value={buyerInfo.buyer_email} onChange={e => setBuyerInfo({ ...buyerInfo, buyer_email: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-1">
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Tipo Doc.</label>
                                <select className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg" value={buyerInfo.document_type} onChange={e => setBuyerInfo({ ...buyerInfo, document_type: e.target.value })}>
                                    <option value="DNI">DNI</option>
                                    <option value="CUIT">CUIT</option>
                                    <option value="CUIL">CUIL</option>
                                    <option value="LC">LC</option>
                                    <option value="LE">LE</option>
                                    <option value="CI">CI</option>
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Nro Documento (Comprador)</label>
                                <input required type="text" maxLength="20" className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg" value={buyerInfo.document_number} onChange={e => setBuyerInfo({ ...buyerInfo, document_number: e.target.value.replace(/\D/g, '') })} />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-700 border-b pb-2">Contacto</h3>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-1">
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Cod. Area</label>
                                <input required type="tel" maxLength="4" className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg" value={buyerInfo.buyer_area_code} onChange={e => setBuyerInfo({ ...buyerInfo, buyer_area_code: e.target.value.replace(/\D/g, '') })} />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Telefono (sin 15)</label>
                                <input required type="tel" maxLength="15" className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg" value={buyerInfo.buyer_phone} onChange={e => setBuyerInfo({ ...buyerInfo, buyer_phone: e.target.value.replace(/\D/g, '') })} />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-700 border-b pb-2">Detalles del Pedido</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Cantidad (max {maxTickets})</label>
                                <input required type="number" min="1" max={maxTickets} className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg" value={buyerInfo.quantity} onChange={e => setBuyerInfo({ ...buyerInfo, quantity: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Cod. Descuento</label>
                                <input type="text" className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg uppercase" placeholder="Opcional" value={buyerInfo.discount_code} onChange={e => setBuyerInfo({ ...buyerInfo, discount_code: e.target.value.toUpperCase() })} />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <h3 className="text-sm font-bold text-gray-700 border-b pb-2">Asistentes de las entradas (obligatorio)</h3>
                        <p className="text-xs text-gray-500">Se requiere DNI y nombre por entrada. Los DNI deben ser unicos dentro del evento.</p>
                        {attendees.map((att, idx) => (
                            <div key={idx} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div className="sm:col-span-1">
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Entrada {idx + 1} - DNI</label>
                                    <input type="text" maxLength="10" className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg" placeholder="12345678" value={att.dni} onChange={e => {
                                        const newArr = [...attendees];
                                        newArr[idx] = { ...newArr[idx], dni: e.target.value.replace(/\D/g, '') };
                                        setAttendees(newArr);
                                    }} />
                                    {getFieldError(`attendees.${idx}.dni`) && <p className="text-xs text-red-600 mt-1">{getFieldError(`attendees.${idx}.dni`)}</p>}
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Entrada {idx + 1} - Nombre y apellido</label>
                                    <input type="text" maxLength="255" className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg" placeholder="Nombre del asistente" value={att.name} onChange={e => {
                                        const newArr = [...attendees];
                                        newArr[idx] = { ...newArr[idx], name: e.target.value };
                                        setAttendees(newArr);
                                    }} />
                                    {getFieldError(`attendees.${idx}.name`) && <p className="text-xs text-red-600 mt-1">{getFieldError(`attendees.${idx}.name`)}</p>}
                                </div>
                            </div>
                        ))}
                        {fieldErrors.attendees && <p className="text-xs text-red-600">{Array.isArray(fieldErrors.attendees) ? fieldErrors.attendees[0] : fieldErrors.attendees}</p>}
                    </div>

                    <div className="space-y-3">
                        <h3 className="text-sm font-bold text-gray-700 border-b pb-2">Competidor (opcional)</h3>
                        {linkedCompetitorDni ? (
                            <>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">DNI del competidor (desde el link)</label>
                                <input type="text" readOnly value={competitorDni} className="mt-1 w-full p-2.5 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed" />
                                <p className="text-xs text-gray-500">Este DNI fue precargado por la plataforma del competidor y no puede modificarse.</p>
                            </>
                        ) : (
                            <>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">DNI del competidor</label>
                                <input type="text" maxLength="10" className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg" placeholder="Opcional" value={competitorDni} onChange={e => setCompetitorDni(e.target.value.replace(/\D/g, ''))} />
                                {invalidCompetitorParam && <p className="text-xs text-amber-600">El link no contiene un DNI de competidor valido (7-10 digitos). Podes continuar con la compra normalmente.</p>}
                            </>
                        )}
                    </div>

                    <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/50">
                        <button type="button" onClick={() => setShowAddress(!showAddress)} className="flex items-center justify-between w-full text-left text-sm font-semibold text-indigo-600">
                            <span>{showAddress ? 'Quitar direccion de facturacion' : 'Agregar direccion de facturacion (Opcional)'}</span>
                        </button>
                        {showAddress && (
                            <div className="mt-4 space-y-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Calle</label>
                                    <input type="text" className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg bg-white" value={buyerInfo.buyer_street_name} onChange={e => setBuyerInfo({ ...buyerInfo, buyer_street_name: e.target.value })} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Numero</label>
                                        <input type="text" className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg bg-white" value={buyerInfo.buyer_street_number} onChange={e => setBuyerInfo({ ...buyerInfo, buyer_street_number: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Codigo Postal</label>
                                        <input type="text" className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg bg-white" value={buyerInfo.buyer_zip_code} onChange={e => setBuyerInfo({ ...buyerInfo, buyer_zip_code: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="pt-2">
                        <button type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3.5 px-4 rounded-xl disabled:bg-gray-400">
                            {loading ? 'Procesando pago...' : 'Pagar con Mercado Pago'}
                        </button>
                        <button type="button" disabled={loading} onClick={() => navigate(`/sales${competitorQuery}`)} className="w-full text-gray-500 text-sm mt-3 hover:text-indigo-600">
                            Volver a la seleccion de evento
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
